import React, { useState, useMemo, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Trash2, Save, Plus, FileText, Search, User as UserIcon, Warehouse, Calendar as CalendarIcon, Globe, Info, Truck, UserSquare, Clock } from "lucide-react";
import { CustomSelectInput } from "../../../components/CustomSelectInput";
import { CustomDateInput } from "../../../components/CustomDateInput";
import { IconButton, TextButton } from "../../../components/ButtonComponents";
import { useSelectDialog } from "../../../hooks/useSelectDialog";
import { useCustomerStore } from "../../../store/customerStore";
import { useProductStore } from "../../../store/productStore";
import { useWarehouseStore } from "../../../store/warehouse";
import { useCurrencyStore } from "../../../store/currencyStore";
import { Button } from "primereact/button";
import { toast } from "react-toastify";
import { RollTransactionModal } from "../../../hooks/RollTransactionModal";

interface DespatchDialogProps {
    isOpen: boolean;
    onClose: () => void;
    type: 'PURCHASE' | 'SALES'; // SALES: Sevk İrsaliyesi (Çıkış), PURCHASE: Giriş İrsaliyesi (Giriş)
    onSave: (data: any) => void;
    initialData?: any;
}

export const DespatchDialog: React.FC<DespatchDialogProps> = ({ isOpen, onClose, type, onSave, initialData }) => {
    const { fetchDataDynamic: fetchCustomers } = useCustomerStore();
    const { fetchDataDynamic: fetchStocks } = useProductStore();
    const { fetchDataDynamic: fetchWarehouses } = useWarehouseStore();
    const { getLiveRates } = useCurrencyStore();
    const customerSelect = useSelectDialog<any>();
    const stockSelect = useSelectDialog<any>();
    const warehouseSelect = useSelectDialog<any>();

    const [isRollModalOpen, setIsRollModalOpen] = useState(false);
    const [selectedItemForRolls, setSelectedItemForRolls] = useState<any>(null);

    const [despatch, setDespatch] = useState({
        accountId: null as number | null,
        accountName: "",
        vknTckn: "",
        warehouseId: null as number | null,
        warehouseName: "",
        date: new Date(),
        transportDate: new Date(), // Fiili Sevk Tarihi
        reference: "",
        description: "",
        currency: "TRY",
        exchangeRate: 1,
        items: [] as any[],
        // İrsaliye Özel Alanları
        transporterName: "",
        transporterTcknVkn: "",
        eInvoiceProfile: "TEMELIRSALIYE", // TEMELIRSALIYE (E-İrsaliye) veya MATBU (Kağıt)
    });

    useEffect(() => {
        const updateRate = async () => {
            if (!initialData && isOpen && despatch.currency !== 'TRY') {
                try {
                    const rates = await getLiveRates();
                    const currencyKey = despatch.currency as keyof typeof rates;
                    const newRate = rates[currencyKey] || 1;
                    setDespatch(prev => ({ ...prev, exchangeRate: newRate }));
                } catch (error) { console.error("Kur çekilemedi:", error); }
            } else if (despatch.currency === 'TRY') {
                setDespatch(prev => ({ ...prev, exchangeRate: 1 }));
            }
        };
        updateRate();
    }, [despatch.currency, isOpen, initialData]);

    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                setDespatch({
                    ...initialData,
                    accountId: initialData.accountId,
                    accountName: initialData.account?.name || "",
                    warehouseId: initialData.stockMovements?.[0]?.warehouseId || null,
                    warehouseName: initialData.stockMovements?.[0]?.warehouse?.name || "Varsayılan Depo",
                    date: new Date(initialData.date),
                    transportDate: initialData.transportDate ? new Date(initialData.transportDate) : new Date(initialData.date),
                    reference: initialData.reference || "",
                    description: initialData.description || "",
                    currency: initialData.currency || "TRY",
                    exchangeRate: initialData.exchangeRate || 1,
                    transporterName: initialData.transporterName || "",
                    transporterTcknVkn: initialData.transporterTcknVkn || "",
                    eInvoiceProfile: initialData.eInvoiceProfile || "TEMELIRSALIYE",
                    items: initialData.stockMovements?.map((sm: any) => ({
                        id: sm.id,
                        stockId: sm.stockId,
                        variantId: sm.variantId,
                        stockName: sm.variantId ? `${sm.stock?.name} (${sm.variant?.name})` : sm.stock?.name,
                        quantity: Math.abs(sm.quantity),
                        unitPrice: sm.unitPrice,
                        taxRate: sm.taxRate,
                        discountRate: sm.discountRate || 0,
                        rollTransactions: sm.rollTransactions || []
                    })) || []
                });
            } else {
                setDespatch(prev => ({
                    ...prev, accountId: null, accountName: "", items: [],
                    date: new Date(), transportDate: new Date(),
                    reference: "", description: "", transporterName: "", transporterTcknVkn: ""
                }));
                fetchWarehouses({ pageIndex: 1, pageSize: 5 }).then(res => {
                    if (res?.items?.length > 0) {
                        setDespatch(prev => ({ ...prev, warehouseId: res.items[0].id, warehouseName: res.items[0].name }));
                    }
                });
            }
        }
    }, [isOpen, initialData]);

    const summary = useMemo(() => {
        let base = 0, tax = 0, discount = 0;
        despatch.items.forEach(item => {
            const lineBase = (item.quantity * item.unitPrice);
            const lineDiscount = lineBase * (item.discountRate / 100);
            const lineTax = (lineBase - lineDiscount) * (item.taxRate / 100);
            base += lineBase; discount += lineDiscount; tax += lineTax;
        });
        const total = base - discount + tax;
        return { base, tax, discount, total, totalTRY: total * despatch.exchangeRate };
    }, [despatch.items, despatch.exchangeRate]);

    const handleSave = () => {
        if (!despatch.accountId) { toast.error("Lütfen bir cari hesap seçiniz."); return; }
        if (despatch.items.length === 0) { toast.error("İrsaliyeye en az bir ürün eklemelisiniz."); return; }
        if (!despatch.warehouseId) { toast.error("Lütfen bir depo seçiniz."); return; }

        const payload = {
            id: initialData?.id,
            accountId: despatch.accountId,
            type: "WAYBILL",
            invoiceType: type === 'SALES' ? 'SALES' : 'PURCHASE',
            direction: type === 'SALES' ? 'OUT' : 'IN',
            currency: despatch.currency,
            exchangeRate: despatch.exchangeRate,
            amount: summary.total,
            baseAmount: summary.base,
            taxAmount: summary.tax,
            discountAmount: summary.discount,
            reference: despatch.reference,
            description: despatch.description,
            date: despatch.date,
            dueDate: despatch.date, // İrsaliyede vade genelde tarihle aynı tutulur
            eInvoiceProfile: despatch.eInvoiceProfile,
            isDespatch: true,

            // Lojistik Alanları
            transporterName: despatch.transporterName,
            transporterTcknVkn: despatch.transporterTcknVkn,
            transportDate: despatch.transportDate,

            stockMovements: despatch.items.map(item => {
                const movement: any = {
                    stockId: item.stockId,
                    warehouseId: despatch.warehouseId,
                    quantity: item.quantity,
                    unitPrice: item.unitPrice,
                    taxRate: item.taxRate,
                    discountRate: item.discountRate,
                    totalAmount: (item.quantity * item.unitPrice) * (1 - item.discountRate / 100) * (1 + item.taxRate / 100),
                    type: type === 'SALES' ? 'OUT' : 'IN'
                };
                if (item.rollTransactions && item.rollTransactions.length > 0) {
                    movement.rollTransactions = item.rollTransactions;
                }
                if (item.variantId && item.variantId !== 0) {
                    movement.variantId = Number(item.variantId);
                }
                return movement;
            })
        };
        onSave(payload);
    };

    const addLine = async () => {
        const res = await fetchStocks({ pageIndex: 1, pageSize: 50 });
        const selected = await stockSelect.open({
            title: "Ürün / Varyant Seç",
            data: res.items,
            columns: [
                { field: "sku", header: "SKU" },
                { field: "name", header: "Ürün Adı" },
                { field: "quantity", header: "Stok" }
            ],
            rowExpansionTemplate: (stock: any) => (
                <div className="p-3">
                    <DataTable value={stock.variants} className="p-datatable-sm shadow-1 rounded-lg">
                        <Column field="name" header="Varyant" />
                        <Column field="sku" header="Barkod" />
                        <Column body={(variant) => (
                            <Button type="button" label="Seç" icon="pi pi-check" className="p-button-sm p-button-info"
                                onClick={(e) => {
                                    e.preventDefault(); e.stopPropagation();
                                    stockSelect.selectItem({ ...stock, selectedVariant: variant });
                                }}
                            />
                        )} />
                    </DataTable>
                </div>
            )
        });

        if (selected) {
            const isVariant = !!selected.selectedVariant;
            const defaultPrice = selected.prices?.length > 0
                ? (type === 'SALES' ? selected.prices[0].salePrice : selected.prices[0].purchasePrice)
                : (type === 'SALES' ? (selected.salePrice || 0) : (selected.purchasePrice || 0));

            const newItem = {
                id: Math.random().toString(36).substr(2, 9),
                stockId: isVariant ? selected.selectedVariant.stockId : selected.id,
                variantId: isVariant ? selected.selectedVariant.id : null,
                stockName: isVariant ? `${selected.name} (${selected.selectedVariant.name})` : selected.name,
                quantity: 1,
                unitPrice: defaultPrice,
                taxRate: selected.taxRate || 20,
                discountRate: 0,
                rollTransactions: []
            };
            setDespatch(prev => ({ ...prev, items: [...prev.items, newItem] }));
        }
    };

    const openRollsDialog = (row: any) => {
        if (!despatch.warehouseId) { toast.error("Lütfen önce bir depo seçiniz."); return; }
        setSelectedItemForRolls(row);
        setIsRollModalOpen(true);
    };

    const handleRollsComplete = (updatedRolls?: any[]) => {
        if (updatedRolls && selectedItemForRolls) {
            const totalQuantity = updatedRolls.reduce((acc, curr) => acc + (parseFloat(curr.quantity) || 0), 0);
            setDespatch(prev => ({
                ...prev,
                items: prev.items.map(i => i.id === selectedItemForRolls.id ? { ...i, quantity: totalQuantity, rollTransactions: updatedRolls } : i)
            }));
        }
        setIsRollModalOpen(false);
        setSelectedItemForRolls(null);
    };

    return (
        <Dialog
            maximizable resizable={false}
            header={
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${type === 'SALES' ? 'bg-amber-100 text-amber-600' : 'bg-blue-100 text-blue-600'}`}>
                        <FileText size={20} />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold leading-none">
                            {initialData ? 'İrsaliyeyi Düzenle' : (type === 'SALES' ? 'Yeni Sevk İrsaliyesi' : 'Yeni Giriş İrsaliyesi')}
                        </h2>
                        <p className="text-xs mt-1 text-[var(--text)] opacity-60">{initialData ? `${despatch.reference} düzenleniyor` : 'Depo hareket belgesi oluşturuluyor...'}</p>
                    </div>
                </div>
            }
            visible={isOpen} onHide={onClose} maximized
            footer={
                <div className="flex justify-between items-center w-full gap-4 p-2">
                    <div className="flex gap-8">
                        <div className="flex flex-col items-start">
                            <span className="text-[10px] uppercase font-bold mb-1 text-[var(--text)] opacity-60">Ara Toplam</span>
                            <span className="text-md font-semibold text-[var(--text)]">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: despatch.currency }).format(summary.base)}</span>
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-[10px] uppercase font-bold mb-1 text-[var(--text)] opacity-60">Genel Toplam</span>
                            <span className="text-lg font-black text-[var(--primary)]">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: despatch.currency }).format(summary.total)}</span>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <TextButton onClick={onClose} className="btn-cancel">Vazgeç</TextButton>
                        <TextButton onClick={handleSave} className="flex items-center gap-2 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white px-4 py-2 rounded-lg transition-colors">
                            <Save size={18} /><span>İrsaliyeyi Kaydet</span>
                        </TextButton>
                    </div>
                </div>
            }
        >
            <div className="flex flex-col gap-4 p-2">
                {/* Üst Temel Bilgiler */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 p-4 bg-[var(--card)] border border-[var(--border)] rounded-xl shadow-sm">
                    <div className="flex flex-col gap-1.5">
                        <CustomSelectInput
                            label={<><UserIcon size={12} /> Cari Hesap</>}
                            value={despatch.accountName || ""}
                            placeholder="Cari Seçiniz..."
                            icon={Search}
                            onClick={async () => {
                                const res = await fetchCustomers({ pageIndex: 1, pageSize: 20 });
                                const selected = await customerSelect.open({ title: "Cari Seç", data: res.items, columns: [{ field: "name", header: "Cari Adı" }] });
                                if (selected) setDespatch(prev => ({ ...prev, accountId: selected.id, accountName: selected.name, vknTckn: selected.taxNumber }));
                            }}
                            onClear={() => setDespatch(prev => ({ ...prev, accountId: 0, accountName: "", vknTckn: "" }))}
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <CustomSelectInput
                            label={<><Warehouse size={12} /> Depo</>}
                            value={despatch.warehouseName || ""}
                            placeholder="Depo Seçiniz..."
                            icon={Warehouse}
                            onClick={async () => {
                                const res = await fetchWarehouses({ pageIndex: 1, pageSize: 10 });
                                const selected = await warehouseSelect.open({ title: "Depo Seç", data: res.items, columns: [{ field: "name", header: "Depo Adı" }] });
                                if (selected) setDespatch(prev => ({ ...prev, warehouseId: selected.id, warehouseName: selected.name }));
                            }}
                            onClear={() => setDespatch(prev => ({ ...prev, warehouseId: 0, warehouseName: "" }))}
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <CustomDateInput
                            label={<><CalendarIcon size={12} /> Düzenleme Tarihi</>}
                            value={despatch.date}
                            onChange={(val) => setDespatch(prev => ({ ...prev, date: val as Date }))}
                            selectionMode="single"
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <label className="text-[11px] font-bold uppercase flex items-center gap-1 text-[var(--text)]"><Globe size={12} /> Döviz / Kur</label>
                        <div className="flex gap-1 h-10 w-full">
                            <Dropdown value={despatch.currency} options={[{ label: 'TRY', value: 'TRY' }, { label: 'USD', value: 'USD' }, { label: 'EUR', value: 'EUR' }]} onChange={(e) => setDespatch(prev => ({ ...prev, currency: e.value }))} className="w-2/3 font-bold text-sm bg-[var(--input-bg)] border-[var(--input-border)] text-[var(--input-text)]" />
                            <InputNumber value={despatch.exchangeRate} onValueChange={(e) => setDespatch(prev => ({ ...prev, exchangeRate: e.value || 1 }))} disabled={despatch.currency === 'TRY'} className="w-2/3" inputClassName="w-full text-right pr-2 text-xs bg-[var(--input-bg)] border-[var(--input-border)] text-[var(--input-text)]" />
                        </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <label className="text-[11px] font-bold uppercase flex items-center gap-1 text-[var(--text)]"><Info size={12} /> Referans / Not</label>
                        <InputText value={despatch.description} onChange={(e) => setDespatch(prev => ({ ...prev, description: e.target.value }))} placeholder="Not..." className="h-10 w-full bg-[var(--input-bg)] border-[var(--input-border)] text-[var(--input-text)]" />
                    </div>
                </div>

                {/* 🌟 Lojistik ve Taşıyıcı Bilgileri Paneli */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-[var(--card)] border border-[var(--border)] rounded-xl shadow-sm mt-2">
                    <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-bold uppercase flex items-center gap-1 text-[var(--text)]"><Truck size={12} /> Taşıyıcı Firma / Şoför Adı</label>
                        <InputText value={despatch.transporterName} onChange={(e) => setDespatch(p => ({ ...p, transporterName: e.target.value }))} placeholder="Örn: Aras Kargo veya Ahmet Yılmaz" className="p-inputtext-sm w-full bg-[var(--input-bg)] border-[var(--input-border)] text-[var(--input-text)]" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-bold uppercase flex items-center gap-1 text-[var(--text)]"><UserSquare size={12} /> Taşıyıcı VKN/TCKN</label>
                        <InputText value={despatch.transporterTcknVkn} onChange={(e) => setDespatch(p => ({ ...p, transporterTcknVkn: e.target.value }))} placeholder="Vergi No veya TCKN" className="p-inputtext-sm w-full bg-[var(--input-bg)] border-[var(--input-border)] text-[var(--input-text)]" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <CustomDateInput
                            label="Fiili Sevk Tarihi / Saati"
                            value={despatch.transportDate}
                            onChange={(val) => setDespatch(p => ({ ...p, transportDate: val as Date }))}
                            selectionMode="single"
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-bold uppercase flex items-center gap-1 text-[var(--text)]"><FileText size={12} /> Belge Türü</label>
                        <Dropdown value={despatch.eInvoiceProfile} options={[{ label: 'E-İrsaliye', value: 'TEMELIRSALIYE' }, { label: 'Kağıt/Matbu İrsaliye', value: 'MATBU' }]} onChange={(e) => setDespatch(p => ({ ...p, eInvoiceProfile: e.value }))} className="p-inputtext-sm w-full bg-[var(--input-bg)] border-[var(--input-border)] text-[var(--input-text)]" />
                    </div>
                </div>

                {/* İrsaliye Kalemleri Tablosu */}
                <div className="flex-1 rounded-xl border border-[var(--border)] overflow-hidden flex flex-col mt-2">
                    <div className="p-3 flex justify-between items-center border-b border-[var(--border)] bg-[var(--bg-alt)]">
                        <div className="flex items-center gap-2">
                            <span className="font-bold text-[var(--text)]">Sevk Kalemleri</span>
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-[var(--card)] border border-[var(--border)] text-[var(--text)]">{despatch.items.length} Kalem</span>
                        </div>
                        <TextButton onClick={addLine} className="flex items-center gap-2 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white h-8 px-3 rounded text-xs transition-colors"><Plus size={14} />Satır Ekle</TextButton>
                    </div>
                    <div className="flex-1   bg-[var(--card)]">
                        <DataTable value={despatch.items} className="p-datatable-sm" rowHover emptyMessage={<div className="p-4 text-center text-[var(--text)] opacity-60">Ürün eklenmedi.</div>}>
                            <Column header="ÜRÜN / VARYANT" style={{ minWidth: '250px' }} body={(row) => (
                                <div className="flex flex-col gap-1">
                                    <span className="font-semibold text-[var(--text)]">{row.stockName}</span>
                                    {row.variantId && <span className="text-[9px] px-1.5 py-0.5 bg-[var(--bg-alt)] text-[var(--text)] rounded border border-[var(--border)] w-fit font-bold uppercase">Varyant</span>}
                                </div>
                            )} />
                            <Column header="MİKTAR / TOPLAR" style={{ width: '180px' }} body={(row) => (
                                <div className="flex items-center gap-2">
                                    <InputNumber value={row.quantity} onValueChange={(e) => setDespatch(prev => ({ ...prev, items: prev.items.map(i => i.id === row.id ? { ...i, quantity: e.value } : i) }))} disabled={row.rollTransactions?.length > 0} buttonLayout="horizontal" className="w-20" inputClassName="w-12 text-center font-bold text-[var(--primary)] bg-[var(--input-bg)] border-[var(--input-border)]" min={1} />
                                    <Button type="button" label={row.rollTransactions?.length > 0 ? `${row.rollTransactions.length} Top` : "Top"} icon="pi pi-barcode" className={`p-button-sm px-2 py-1 ${row.rollTransactions?.length > 0 ? 'p-button-success' : 'p-button-outlined'}`} onClick={() => openRollsDialog(row)} />
                                </div>
                            )} />
                            <Column header="BİRİM FİYAT" style={{ width: '130px' }} body={(row) => (
                                <InputNumber value={row.unitPrice} onValueChange={(e) => setDespatch(prev => ({ ...prev, items: prev.items.map(i => i.id === row.id ? { ...i, unitPrice: e.value } : i) }))} mode="currency" currency={despatch.currency} className="w-full" inputClassName="w-full font-medium text-right text-sm bg-[var(--input-bg)] border-[var(--input-border)] text-[var(--input-text)]" />
                            )} />
                            <Column header="KDV (%)" style={{ width: '80px' }} body={(row) => (
                                <InputNumber value={row.taxRate} onValueChange={(e) => setDespatch(prev => ({ ...prev, items: prev.items.map(i => i.id === row.id ? { ...i, taxRate: e.value } : i) }))} prefix="%" className="w-full" inputClassName="w-full text-center text-sm bg-[var(--input-bg)] border-[var(--input-border)] text-[var(--input-text)]" />
                            )} />
                            <Column header="İSK (%)" style={{ width: '80px' }} body={(row) => (
                                <InputNumber value={row.discountRate} onValueChange={(e) => setDespatch(prev => ({ ...prev, items: prev.items.map(i => i.id === row.id ? { ...i, discountRate: e.value } : i) }))} prefix="%" className="w-full" inputClassName="w-full text-center text-sm bg-[var(--input-bg)] border-[var(--input-border)] text-[var(--input-text)]" />
                            )} />
                            <Column header="TOPLAM" align="right" className="font-bold text-[var(--text)]" style={{ width: '130px' }} body={(row) => {
                                const total = (row.quantity * row.unitPrice) * (1 - row.discountRate / 100) * (1 + row.taxRate / 100);
                                return <span>{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: despatch.currency }).format(total)}</span>
                            }} />
                            <Column style={{ width: '50px' }} body={(row) => (
                                <IconButton icon={Trash2} onClick={() => setDespatch(prev => ({ ...prev, items: prev.items.filter(i => i.id !== row.id) }))} className="text-[var(--danger)] hover:bg-[var(--danger-hover)]" />
                            )} />
                        </DataTable>
                    </div>
                </div>
            </div>
            {customerSelect.modal} {stockSelect.modal} {warehouseSelect.modal}

            {selectedItemForRolls && (
                <RollTransactionModal
                    isOpen={isRollModalOpen}
                    onClose={handleRollsComplete}
                    type={type === 'SALES' ? 'OUT' : 'IN'}
                    initialRolls={selectedItemForRolls.rollTransactions || []}
                    stockId={selectedItemForRolls.stockId}
                    warehouseId={despatch.warehouseId!}
                    variantId={selectedItemForRolls.variantId}
                />
            )}
        </Dialog>
    );
};